import { Provider } from "react-redux";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useMemo, useEffect } from "react";
import ReactGA from "react-ga4";
import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import FloatingMessage from "./components//FloatingMessage/FloatingMessage.js";

import Home from "../src/screens/Home/Home";
import "@fontsource/roboto-condensed"; // Defaults to weight 400
import "@fontsource/roboto-condensed/400.css"; // Specify weight
import "@fontsource/roboto-condensed/400-italic.css"; // Specify weight and style
import "@fontsource/roboto"; // Defaults to weight 400
import "@fontsource/roboto/400.css"; // Specify weight
import "@fontsource/roboto/400-italic.css"; // Specify weight and style

import "./index.css";

import firebaseConfig from "./db/firebaseConfig.js";
import Routes from "./routes";
import { store } from "./redux/store";
import { setUserData } from "./redux/user";
import { setConfig, setDisableCanLogin } from "./redux/global";
import { setUserLoading } from "./redux/loading.js";
import {
  setRafflesData,
  setRafflesVersions,
  setRafflesWinnersData,
  setRafflesLuckyNumbers
} from "./redux/raffles.js";
import getConfigFile from "./config/configFile";
import ApiService from "./services/ApiService";
import UserHelper from "./utils/UserHelper";
import DataHelper from "./utils/DataHelper";
import { useApiContext } from "./hooks/useApiContext";
import MessageHelper from "./utils/MessageHelper";
import Loader from "./components/Loader/Loader.js";
import ToolHelper from "./utils/ToolHelper.js";
import Popup from "./components/Molecules/Popup.js";

const App = () => {
  const apiService = new ApiService("");
  let app = initializeApp(firebaseConfig);
  let db = getFirestore(app);

  let userHelper = useMemo(() => new UserHelper(app, db, getConfigFile), []);
  let dataHelper = useMemo(() => new DataHelper(app, db, getConfigFile), []);
  userHelper.setDataHelper(dataHelper);
  dataHelper.setUserHelper(userHelper);

  const notification = MessageHelper.useNotification();
  const message = MessageHelper.useMessage();
  const popup = ToolHelper.usePopup();

  useApiContext(apiService, dataHelper);
  const dispatch = useDispatch();

  if (firebaseConfig.trackingId || firebaseConfig.measurementId) {
    ReactGA.initialize(
      firebaseConfig.trackingId || firebaseConfig.measurementId
    );
  }

  const [hasAuthResponse, setHasAuthResponse] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authObserver, setAuthObserver] = useState(null);
  const [unsubPriorityFunctions, setUnsubPriorityFunctions] = useState({});
  const [unsubFunctions, setUnsubFunctions] = useState({});
  const [currentRaffle, setCurrentRaffle] = useState(null);
  const [userId, setUserId] = useState(null);
  const loadingData = useSelector((state) => state.loading);

  const disableCanLogin = useSelector((state) => state.global?.disableCanLogin);
  const { userData } = useSelector((state) => state.user);
  
  const loadDisableLoginData = async () => {
    await dataHelper.disableLogin((value) => dispatch(setDisableCanLogin(value)));
  }

  const startAuthObserver = async () => {
    apiService.setBaseUrl(await dataHelper.getHerokuDomain());
    if (!authObserver) {
      const authState = onAuthStateChanged(getAuth(), async () => {
        let userRef = await userHelper.getUserFirebaseId();
        setHasAuthResponse(true);
        await loadData();
        if (userRef) {
          global.isAuthenticated = true;
          setIsAuthenticated(true);
          await loadPriorityData();
        } else {
          global.isAuthenticated = false;
          setIsAuthenticated(false);
          
        }
      });

      setAuthObserver(authState);
      setUnsubFunctions((us) => {
        us["authState" + new Date().getTime()] = authState;
        return us;
      });
    }
  };

  const windowMessageHandler = (params) => {
    if (params?.data?.messageType === "notification-clicked") {
      notification.add(params.data.notification, params.data.data);
    }
  };

  const unsubscribePriorityFunctions = () => {
    if (
      unsubPriorityFunctions &&
      Object.keys(unsubPriorityFunctions).length > 0
    ) {
      let unsubArray = Object.entries(unsubPriorityFunctions);

      unsubArray.forEach((unsub) => {
        if (unsub[1] && typeof unsub[1] === "function") {
          unsub[1]();
        }
      });

      setUnsubFunctions({});
    }

    navigator.serviceWorker.removeEventListener(
      "message",
      windowMessageHandler
    );
  };

  const unsubscribe = () => {
    if (unsubFunctions && Object.keys(unsubFunctions).length > 0) {
      let unsubArray = Object.entries(unsubFunctions);

      unsubArray.forEach((unsub) => {
        if (unsub[1] && typeof unsub[1] === "function") {
          unsub[1]();
        }
      });

      setUnsubFunctions({});
    }

    navigator.serviceWorker.removeEventListener(
      "message",
      windowMessageHandler
    );
  };

  const loadDataWinners = (raffleVersions) => {
    let data = [];

    raffleVersions.map((raffleVersion) => {
      let productsRaffle = []
      let winnersRaffle = []
      const { Products__r: products, RaffleVersionLuckyNumbers__r: luckyNumbers } = raffleVersion;
      products?.records.map((product) => {
        productsRaffle.push(product)
      })
      productsRaffle = productsRaffle.sort((a, b) => a?.Order__c - b?.Order__c);
      luckyNumbers?.records.map((luckyNumber) => {
        if(luckyNumber?.Status__c === "Winner"){
          const {LuckyNumber__r, Product__r } = luckyNumber;
          winnersRaffle.push({
            Number__c: LuckyNumber__r?.Number__c,
            Name: LuckyNumber__r?.Contact__r?.Name,
            ProductName: Product__r?.Name 
          })
        }
      })
      data.push({
        products: productsRaffle,
        winners: winnersRaffle,
        Date: raffleVersion.EndDate__c,
        EndDate__c: raffleVersion.EndDate__c,
        backgroundImageUrl__c: raffleVersion.BackgroundImage__c
      });
      
    })
    dispatch(setRafflesWinnersData(data));
  }

  const loadPriorityData = async () => {
    unsubscribePriorityFunctions();
    let userRef = await userHelper.snapUser((data) => {
      setUserId(data.FirebaseId__c)
      dispatch(setUserData(data));
      dispatch(setUserLoading(false));
    });
    let rafflesRef = userHelper.snapRaffles(async (rafflesData) => {
      setCurrentRaffle(rafflesData.id)
      dispatch(setRafflesData(rafflesData || []))
      dispatch(setRafflesVersions(rafflesData?.raffleVersionsData || []));
      loadDataWinners(rafflesData?.raffleVersionsData || []);
    });
    let unsubT = { ...userRef, ...rafflesRef};
    setUnsubPriorityFunctions((us) => ({ ...us, ...unsubT }));
  };

  const loadData = async () => {
   await loadDisableLoginData();
   let rafflesRef = userHelper.snapRaffles(async (rafflesData) => {
      setCurrentRaffle(rafflesData.id)
      dispatch(setRafflesData(rafflesData || []))
      dispatch(setRafflesVersions(rafflesData?.raffleVersionsData || []));
      loadDataWinners(rafflesData?.raffleVersionsData || []);
    });
    let unsubT = { ...rafflesRef };
    setUnsubFunctions((us) => ({ ...unsubT }));
  };

  useEffect(() => {
    if (userId && currentRaffle) {
      let luckyNumbersRef = userHelper.snapRafflesLuckyNumbers((data) => {
        dispatch(setRafflesLuckyNumbers(data));
      }, currentRaffle, userId);
      let unsubT = { ...luckyNumbersRef };
      setUnsubPriorityFunctions((us) => ({ ...us, ...unsubT }));
    }
  }, [userId, currentRaffle])

  useEffect(() => {
    setTimeout(() => ReactGA?.send("pageview"), 200);
    setHasAuthResponse(false);
    startAuthObserver();
    message.closeAllPageMessages();
  }, []);
  useEffect(() => {
    dispatch(
      setConfig({
        userHelper,
        dataHelper,
        message,
        unsubscribe,
        unsubscribePriorityFunctions,
        popup,
      })
    );
  }, [
     userHelper, 
     dataHelper,
     message, dispatch, popup]);

  const isLoading = useMemo(() => {
    if (hasAuthResponse && !isAuthenticated) return false;

    return !hasAuthResponse || loadingData.user;
  }, [hasAuthResponse, loadingData, isAuthenticated]);

  useEffect(() => {
    if (Object.keys(userData).length > 0 && disableCanLogin) {
      dispatch(setUserData({}))
      userHelper?.logout(() => {
        return
        },(error) => {
          return
        })
    }
  }, [disableCanLogin, userData, userHelper]);

  return isLoading ? (
    <Loader />
  ) : (
    <AppContainer>
      <Routes />
      <Popup control={popup} />
      <FloatingMessage control={message} />
    </AppContainer>
  );
};

export default () => (
  <Provider store={store}>
    <App />
  </Provider>
);

const AppContainer = styled.div`
  width: 100vw;
`
